import { type MinijobPayoutEmployeeResponse } from '@app/+authenticated/shared/minijob-payout/services/minijob-payout.model';
import { type Permission } from '@sb/shared/permission/util';
import { toISODateString, type DateRange, type PlainDate } from '@shiftbase-com/ts-shared/utilities';
import { eachMonthOfInterval, startOfMonth } from 'date-fns';

/**
 * The permission the payout endpoint itself enforces; a manager without it is served nothing. One
 * constant, so the step definition's gate and `PayrollMinijobService`'s cannot drift apart.
 */
export const MINIJOB_STEP_PERMISSION: Permission = 'Edit minijob balances';

/**
 * The calendar months a payroll period touches, as month starts. Minijob payouts are booked per
 * whole calendar month (SHB-20987), so a period running Jun 16 – Jul 15 has two months to settle.
 */
export function calendarMonthsInRange(range: DateRange): PlainDate[] {
  return eachMonthOfInterval({ start: startOfMonth(range.start), end: startOfMonth(range.end) }).map((month) =>
    toISODateString(month),
  );
}

/**
 * Employees still to pay out across the months in scope. An employee is counted once however many
 * months they are unpaid in: the step's unit is the employee, not the month, and the manager
 * settles all of their months from the same screen.
 */
export function countOutstandingPayouts(payoutsByMonth: MinijobPayoutEmployeeResponse[][]): number {
  const unpaid = new Set(
    payoutsByMonth
      // Loose on purpose: an absent key counts as unpaid too, matching how the view model reads
      // the same field (`payout ?? <derived default>`).
      .flatMap((employees) => employees.filter((employee) => employee.payout == null))
      .map((employee) => employee.id),
  );

  return unpaid.size;
}

/**
 * The month after `month` that still has an employee unpaid, or `null` when none of the later
 * months does — where the step sends the manager once `month` is saved. `null` too for a month
 * outside `months`: the editor is then showing a month the run no longer covers, and there is no
 * "next" to it. Both arrays are indexed alike, `payoutsByMonth[index]` being `months[index]`'s rows.
 */
export function nextOutstandingMonth(
  months: PlainDate[],
  payoutsByMonth: MinijobPayoutEmployeeResponse[][],
  month: PlainDate,
): PlainDate | null {
  const currentIndex = months.indexOf(month);

  if (currentIndex === -1) {
    return null;
  }

  const nextIndex = payoutsByMonth.findIndex(
    (employees, monthIndex) => monthIndex > currentIndex && employees.some((employee) => employee.payout == null),
  );

  return nextIndex === -1 ? null : (months[nextIndex] ?? null);
}
